import { InteractionStatus, type AccountInfo } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useEffect, useMemo, useState } from 'react';
import getMsalAuthToken from './GetMsalAuthToken';
import MsalAuthProcessStage from './MsalAuthProcessStage';
import checkHeartbeat from '../api/ValidateTokenViaHeartbeat';
import type { User } from './User';
import { type IApiConfig } from '../config/Config';

const mapTokenClaims = (account: AccountInfo | undefined): User | undefined => {
  const tokenClaims = account?.idTokenClaims;
  if (!tokenClaims) return undefined;

  return {
    firstName: tokenClaims?.given_name as string,
    lastName: tokenClaims?.family_name as string,
    fullName: `${tokenClaims?.given_name} ${tokenClaims?.family_name}` as string,
    user: tokenClaims?.user as string,
    userType: tokenClaims?.userType as string,
    agent: tokenClaims?.agent as string,
    agency: tokenClaims?.agency as string,
    roles: tokenClaims?.roles as string[],
  };
};

const CreateScopes = (authAppUri?: string): string[] => {
  if (!authAppUri) {
    console.error('Auth app URI is undefined');
    throw new Error('Auth app URI is undefined');
  }

  return [`${authAppUri}/openid`];
};

const useAuthInitializer = ({
  baseApiUri,
  authAppUri,
}: IApiConfig): [User | undefined, (() => Promise<string>) | undefined] => {
  if (baseApiUri == undefined || authAppUri == undefined) {
    console.error('Base API URI or Auth app URI is undefined');
    throw new Error('Base API URI or Auth app URI is undefined');
  }

  const [msalAuthProcessStage, setMsalAuthProcessStage] = useState(
    MsalAuthProcessStage.MsalInitialized
  );

  const { instance, accounts, inProgress: msalInProgress } = useMsal();

  const account = accounts == null || accounts.length === 0 ? undefined : accounts[0];

  const msalScopes = useMemo(() => CreateScopes(authAppUri), [authAppUri]);

  // Going from Msal Initialized to Auth Redirections Complete when all redirections are complete
  useEffect(() => {
    if (msalAuthProcessStage !== MsalAuthProcessStage.MsalInitialized) return;
    if (msalInProgress !== InteractionStatus.None) return;

    setMsalAuthProcessStage((currentAuthProcessStage) => {
      if (currentAuthProcessStage === MsalAuthProcessStage.MsalInitialized) {
        return MsalAuthProcessStage.AuthRedirectionsComplete;
      }

      return currentAuthProcessStage;
    });
  }, [msalAuthProcessStage, msalInProgress]);

  // Auth redirections complete, performing heartbeat
  useEffect(() => {
    if (!instance) return;
    if (msalAuthProcessStage !== MsalAuthProcessStage.AuthRedirectionsComplete) return;
    if (msalInProgress !== InteractionStatus.None) return;

    if (!baseApiUri) {
      console.error('Base API URI is undefined');
      throw new Error('Base API URI is undefined');
    }

    getMsalAuthToken(account, instance, msalScopes).then((authToken) => {
      if (!authToken) {
        console.error('No auth token available');
        throw new Error('No auth token available');
      }

      checkHeartbeat(baseApiUri, authToken).then((success) => {
        if (!success) {
          console.error('Heartbeat failed');
          throw new Error('Heartbeat failed');
        }

        setMsalAuthProcessStage((currentAuthProcessStage) => {
          if (currentAuthProcessStage === MsalAuthProcessStage.AuthRedirectionsComplete) {
            return MsalAuthProcessStage.Ready;
          }

          return currentAuthProcessStage;
        });
      });
    });
  }, [msalAuthProcessStage, instance, account, msalScopes, baseApiUri, msalInProgress]);

  const user = mapTokenClaims(account);

  const providedGetAuthToken =
    msalAuthProcessStage !== MsalAuthProcessStage.Ready
      ? undefined
      : () => getMsalAuthToken(account, instance, msalScopes);

  return [user, providedGetAuthToken];
};

export default useAuthInitializer;
